import { type MessageId, type TurnId, type ThreadId } from "@t3tools/contracts";
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  measureElement as measureVirtualElement,
  type VirtualItem,
  useVirtualizer,
} from "@tanstack/react-virtual";
import { deriveTimelineEntries } from "../../session-logic";
import { AUTO_SCROLL_BOTTOM_THRESHOLD_PX } from "../../chat-scroll";
import { type TurnDiffSummary } from "../../types";
import { summarizeTurnDiffStats } from "../../lib/turnDiffTree";
import ChatMarkdown from "../ChatMarkdown";
import { ChevronDownIcon, ChevronRightIcon, Loader2Icon, Undo2Icon } from "lucide-react";
import { Button } from "../ui/button";
import { clamp } from "effect/Number";
import { estimateTimelineMessageHeight } from "../timelineHeight";
import { buildVisibleUserMessageText, hasCopyableUserMessageText } from "../ChatView.logic";
import { buildExpandedImagePreview, ExpandedImagePreview } from "./ExpandedImagePreview";
import { ProposedPlanCard } from "./ProposedPlanCard";
import { UploadedWorkspaceFileChips } from "./UploadedWorkspaceFileChips";
import { ChangedFilesTree } from "./ChangedFilesTree";
import { DiffStatLabel, hasNonZeroStat } from "./DiffStatLabel";
import { UserInputHistory } from "./UserInputHistory";
import { ActivityGroup } from "./ActivityGroup";
import { MessageCopyButton } from "./MessageCopyButton";
import { cn } from "~/lib/utils";
import { type TimestampFormat } from "../../appSettings";
import { formatTimestamp } from "../../timestampFormat";

const ALWAYS_UNVIRTUALIZED_TAIL_ROWS = 8;

interface MessagesTimelineProps {
  threadId?: ThreadId;
  hasMessages: boolean;
  isWorking: boolean;
  activeTurnInProgress: boolean;
  showThinkingIndicator: boolean;
  activeTurnStartedAt: string | null;
  scrollContainer: HTMLDivElement | null;
  timelineEntries: ReturnType<typeof deriveTimelineEntries>;
  turnTimingDividers: TurnTimingDivider[];
  onToggleTurnTimingDivider: (dividerId: string) => void;
  turnDiffSummaryByAssistantMessageId: Map<MessageId, TurnDiffSummary>;
  nowIso: string;
  expandedWorkGroups: Record<string, boolean>;
  onToggleWorkGroup: (groupId: string) => void;
  onOpenTurnDiff: (turnId: TurnId, filePath?: string) => void;
  revertTurnCountByUserMessageId: Map<MessageId, number>;
  onRevertUserMessage: (messageId: MessageId) => void;
  isRevertingCheckpoint: boolean;
  onImageExpand: (preview: ExpandedImagePreview) => void;
  markdownCwd: string | undefined;
  onWorkspaceFileLinkClick?: ((workspacePath: string) => void) | undefined;
  resolvedTheme: "light" | "dark";
  timestampFormat: TimestampFormat;
  showToolActivity?: boolean;
  showTurnDiffs?: boolean;
  workspaceRoot: string | undefined;
}

export const MessagesTimeline = memo(function MessagesTimeline({
  threadId,
  hasMessages,
  isWorking,
  activeTurnInProgress,
  showThinkingIndicator,
  activeTurnStartedAt,
  scrollContainer,
  timelineEntries,
  turnTimingDividers,
  onToggleTurnTimingDivider,
  turnDiffSummaryByAssistantMessageId,
  nowIso,
  expandedWorkGroups,
  onToggleWorkGroup,
  onOpenTurnDiff,
  revertTurnCountByUserMessageId,
  onRevertUserMessage,
  isRevertingCheckpoint,
  onImageExpand,
  markdownCwd,
  onWorkspaceFileLinkClick,
  resolvedTheme,
  timestampFormat,
  showToolActivity = true,
  showTurnDiffs = true,
  workspaceRoot,
}: MessagesTimelineProps) {
  const timelineRootRef = useRef<HTMLDivElement | null>(null);
  const [timelineWidthPx, setTimelineWidthPx] = useState<number | null>(null);

  useLayoutEffect(() => {
    const timelineRoot = timelineRootRef.current;
    if (!timelineRoot) return;

    const updateWidth = (nextWidth: number) => {
      setTimelineWidthPx((previousWidth) => {
        if (previousWidth !== null && Math.abs(previousWidth - nextWidth) < 0.5) {
          return previousWidth;
        }
        return nextWidth;
      });
    };

    updateWidth(timelineRoot.getBoundingClientRect().width);

    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      updateWidth(timelineRoot.getBoundingClientRect().width);
    });
    observer.observe(timelineRoot);
    return () => {
      observer.disconnect();
    };
  }, [hasMessages, isWorking, showThinkingIndicator]);

  const rows = useMemo<TimelineRow[]>(() => {
    const nextRows: TimelineRow[] = [];
    const insertedDividerIds = new Set<string>();
    const dividersByBeforeEntryId = new Map<string, TurnTimingDivider[]>();
    for (const divider of turnTimingDividers) {
      if (!divider.beforeEntryId) {
        continue;
      }
      const existing = dividersByBeforeEntryId.get(divider.beforeEntryId) ?? [];
      existing.push(divider);
      dividersByBeforeEntryId.set(divider.beforeEntryId, existing);
    }
    const pushTurnTimingDivider = (divider: TurnTimingDivider) => {
      if (insertedDividerIds.has(divider.id)) {
        return;
      }
      insertedDividerIds.add(divider.id);
      nextRows.push({
        kind: "turn-timing",
        id: `turn-timing:${divider.id}`,
        createdAt: divider.startedAt ?? divider.completedAt,
        divider,
      });
    };

    for (let index = 0; index < timelineEntries.length; index += 1) {
      const timelineEntry = timelineEntries[index];
      if (!timelineEntry) {
        continue;
      }

      for (const divider of dividersByBeforeEntryId.get(timelineEntry.id) ?? []) {
        pushTurnTimingDivider(divider);
      }

      if (timelineEntry.kind === "work") {
        const groupedEntries = [timelineEntry.entry];
        let cursor = index + 1;
        while (cursor < timelineEntries.length) {
          const nextEntry = timelineEntries[cursor];
          if (!nextEntry || nextEntry.kind !== "work") break;
          if (timelineEntry.entry.userInput || nextEntry.entry.userInput) break;
          if (nextEntry.entry.turnId !== timelineEntry.entry.turnId) break;
          if ((dividersByBeforeEntryId.get(nextEntry.id)?.length ?? 0) > 0) break;
          groupedEntries.push(nextEntry.entry);
          cursor += 1;
        }
        const isToolActivity = groupedEntries.every((entry) => !entry.userInput);
        if (!(showToolActivity === false && isToolActivity)) {
          nextRows.push({
            kind: "work",
            id: timelineEntry.id,
            createdAt: timelineEntry.createdAt,
            groupedEntries,
          });
        }
        index = cursor - 1;
        continue;
      }

      if (timelineEntry.kind === "proposed-plan") {
        nextRows.push({
          kind: "proposed-plan",
          id: timelineEntry.id,
          createdAt: timelineEntry.createdAt,
          proposedPlan: timelineEntry.proposedPlan,
        });
        continue;
      }

      nextRows.push({
        kind: "message",
        id: timelineEntry.id,
        createdAt: timelineEntry.createdAt,
        message: timelineEntry.message,
      });
    }

    for (const divider of turnTimingDividers) {
      pushTurnTimingDivider(divider);
    }

    if (showThinkingIndicator) {
      nextRows.push({
        kind: "thinking",
        id: "thinking-indicator",
        createdAt: activeTurnStartedAt,
      });
    }

    return nextRows;
  }, [
    activeTurnStartedAt,
    showThinkingIndicator,
    showToolActivity,
    timelineEntries,
    turnTimingDividers,
  ]);

  const firstUnvirtualizedRowIndex = useMemo(() => {
    const firstTailRowIndex = Math.max(rows.length - ALWAYS_UNVIRTUALIZED_TAIL_ROWS, 0);
    if (!activeTurnInProgress) return firstTailRowIndex;

    const turnStartedAtMs =
      typeof activeTurnStartedAt === "string" ? Date.parse(activeTurnStartedAt) : Number.NaN;
    let firstCurrentTurnRowIndex = -1;
    if (!Number.isNaN(turnStartedAtMs)) {
      firstCurrentTurnRowIndex = rows.findIndex((row) => {
        if (row.kind === "turn-timing" && row.divider.state === "working") return true;
        if (!row.createdAt) return false;
        const rowCreatedAtMs = Date.parse(row.createdAt);
        return !Number.isNaN(rowCreatedAtMs) && rowCreatedAtMs >= turnStartedAtMs;
      });
    }

    if (firstCurrentTurnRowIndex < 0) {
      firstCurrentTurnRowIndex = rows.findIndex(
        (row) => row.kind === "message" && row.message.streaming,
      );
    }

    if (firstCurrentTurnRowIndex < 0) return firstTailRowIndex;

    for (let index = firstCurrentTurnRowIndex - 1; index >= 0; index -= 1) {
      const previousRow = rows[index];
      if (!previousRow || previousRow.kind !== "message") continue;
      if (previousRow.message.role === "user") {
        return Math.min(index, firstTailRowIndex);
      }
      if (previousRow.message.role === "assistant" && !previousRow.message.streaming) {
        break;
      }
    }

    return Math.min(firstCurrentTurnRowIndex, firstTailRowIndex);
  }, [activeTurnInProgress, activeTurnStartedAt, rows]);

  const virtualizedRowCount = clamp(firstUnvirtualizedRowIndex, {
    minimum: 0,
    maximum: rows.length,
  });

  const rowVirtualizer = useVirtualizer({
    count: virtualizedRowCount,
    getScrollElement: () => scrollContainer,
    // Use stable row ids so virtual measurements do not leak across thread switches.
    getItemKey: (index: number) => rows[index]?.id ?? index,
    estimateSize: (index: number) => {
      const row = rows[index];
      if (!row) return 96;
      if (row.kind === "work") return 112;
      if (row.kind === "proposed-plan") return estimateTimelineProposedPlanHeight(row.proposedPlan);
      if (row.kind === "turn-timing") return 40;
      if (row.kind === "thinking") return 36;
      return estimateTimelineMessageHeight(row.message, { timelineWidthPx });
    },
    measureElement: measureVirtualElement,
    useAnimationFrameWithResizeObserver: true,
    overscan: 8,
  });
  useEffect(() => {
    if (timelineWidthPx === null) return;
    rowVirtualizer.measure();
  }, [rowVirtualizer, timelineWidthPx]);
  useEffect(() => {
    rowVirtualizer.shouldAdjustScrollPositionOnItemSizeChange = (_item, _delta, instance) => {
      const viewportHeight = instance.scrollRect?.height ?? 0;
      const scrollOffset = instance.scrollOffset ?? 0;
      const remainingDistance = instance.getTotalSize() - (scrollOffset + viewportHeight);
      return remainingDistance > AUTO_SCROLL_BOTTOM_THRESHOLD_PX;
    };
    return () => {
      rowVirtualizer.shouldAdjustScrollPositionOnItemSizeChange = undefined;
    };
  }, [rowVirtualizer]);
  const pendingMeasureFrameRef = useRef<number | null>(null);
  const onTimelineImageLoad = useCallback(() => {
    if (pendingMeasureFrameRef.current !== null) return;
    pendingMeasureFrameRef.current = window.requestAnimationFrame(() => {
      pendingMeasureFrameRef.current = null;
      rowVirtualizer.measure();
    });
  }, [rowVirtualizer]);
  useEffect(() => {
    return () => {
      const frame = pendingMeasureFrameRef.current;
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, []);

  const virtualRows = rowVirtualizer.getVirtualItems();
  const nonVirtualizedRows = rows.slice(virtualizedRowCount);
  const [allDirectoriesExpandedByTurnId, setAllDirectoriesExpandedByTurnId] = useState<
    Record<string, boolean>
  >({});
  const onToggleAllDirectories = useCallback((turnId: TurnId) => {
    setAllDirectoriesExpandedByTurnId((current) => ({
      ...current,
      [turnId]: !(current[turnId] ?? true),
    }));
  }, []);

  const renderRowContent = (row: TimelineRow) => (
    <div
      className={cn("pb-4", row.kind === "work" && "px-1")}
      data-timeline-row-kind={row.kind}
      data-message-id={row.kind === "message" ? row.message.id : undefined}
      data-message-role={row.kind === "message" ? row.message.role : undefined}
    >
      {row.kind === "work" &&
        (row.groupedEntries[0]?.userInput ? (
          <UserInputHistory
            input={row.groupedEntries[0].userInput}
            open={expandedWorkGroups[row.id] ?? false}
            onOpenChange={() => onToggleWorkGroup(row.id)}
          />
        ) : (
          <ActivityGroup
            threadId={threadId}
            entries={row.groupedEntries}
            open={expandedWorkGroups[row.id] ?? false}
            onOpenChange={() => onToggleWorkGroup(row.id)}
          />
        ))}

      {row.kind === "message" &&
        row.message.role === "user" &&
        (() => {
          const userImages = row.message.attachments ?? [];
          const uploadedFiles = row.message.uploadedFiles ?? [];
          const visibleUserText = buildVisibleUserMessageText({
            prompt: row.message.text,
            uploadedFilesCount: uploadedFiles.length,
          });
          const canCopyUserText = hasCopyableUserMessageText(row.message.text);
          const canRevertAgentWork = revertTurnCountByUserMessageId.has(row.message.id);
          return (
            <div className="flex justify-end">
              <div className="group min-w-0 max-w-[80%]">
                <div className="rounded-2xl rounded-br-sm bg-secondary px-4 py-3">
                  {userImages.length > 0 && (
                    <div className="mb-2 grid max-w-[420px] grid-cols-2 gap-2">
                      {userImages.map(
                        (image: NonNullable<TimelineMessage["attachments"]>[number]) => (
                          <div
                            key={image.id}
                            className="overflow-hidden rounded-lg border border-border/80 bg-background/70"
                          >
                            {image.previewUrl ? (
                              <button
                                type="button"
                                className="h-full w-full cursor-zoom-in"
                                aria-label={`Preview ${image.name}`}
                                onClick={() => {
                                  const preview = buildExpandedImagePreview(userImages, image.id);
                                  if (!preview) return;
                                  onImageExpand(preview);
                                }}
                              >
                                <img
                                  src={image.previewUrl}
                                  alt={image.name}
                                  className="h-full max-h-[220px] w-full object-cover"
                                  onLoad={onTimelineImageLoad}
                                  onError={onTimelineImageLoad}
                                />
                              </button>
                            ) : (
                              <div className="flex min-h-[72px] items-center justify-center px-2 py-3 text-center text-[11px] text-muted-foreground/70">
                                {image.name}
                              </div>
                            )}
                          </div>
                        ),
                      )}
                    </div>
                  )}
                  <UploadedWorkspaceFileChips className="mb-2" files={uploadedFiles} />
                  {visibleUserText && (
                    <ChatMarkdown
                      text={visibleUserText}
                      cwd={markdownCwd}
                      onWorkspaceFileLinkClick={onWorkspaceFileLinkClick}
                    />
                  )}
                </div>
                <div className="mt-1.5 flex items-center justify-end gap-1.5">
                  {canRevertAgentWork && (
                    <Button
                      type="button"
                      size="xs"
                      variant="outline"
                      className="opacity-0 transition-opacity duration-200 focus-visible:opacity-100 group-hover:opacity-100"
                      disabled={isRevertingCheckpoint || isWorking}
                      onClick={() => onRevertUserMessage(row.message.id)}
                      aria-label="Revert to this message"
                      title="Revert to this message"
                    >
                      <Undo2Icon className="size-3" />
                    </Button>
                  )}
                  <p className="text-right text-[10px] text-muted-foreground/80">
                    {formatUserMessageMeta(row.message, timestampFormat)}
                  </p>
                  {canCopyUserText && <MessageCopyButton text={visibleUserText} />}
                </div>
              </div>
            </div>
          );
        })()}

      {row.kind === "turn-timing" && (
        <TurnTimingDividerRow
          divider={row.divider}
          nowIso={nowIso}
          onToggle={onToggleTurnTimingDivider}
        />
      )}

      {row.kind === "thinking" && (
        <div
          className="flex items-center gap-2 px-1 py-1 text-sm text-muted-foreground/70"
          aria-live="polite"
        >
          <Loader2Icon className="size-3.5 animate-spin" aria-hidden="true" />
          <span>Thinking</span>
        </div>
      )}

      {row.kind === "message" &&
        row.message.role === "assistant" &&
        (() => {
          const messageText = row.message.text || (row.message.streaming ? "" : "(empty response)");
          return (
            <>
              <div className="min-w-0 px-1 py-0.5">
                <ChatMarkdown
                  text={messageText}
                  cwd={markdownCwd}
                  isStreaming={Boolean(row.message.streaming)}
                  onWorkspaceFileLinkClick={onWorkspaceFileLinkClick}
                />
                {(() => {
                  const turnSummary = turnDiffSummaryByAssistantMessageId.get(row.message.id);
                  if (!showTurnDiffs || !turnSummary) return null;
                  const checkpointFiles = turnSummary.files;
                  if (checkpointFiles.length === 0) return null;
                  const summaryStat = summarizeTurnDiffStats(checkpointFiles);
                  const changedFileCountLabel = String(checkpointFiles.length);
                  const allDirectoriesExpanded =
                    allDirectoriesExpandedByTurnId[turnSummary.turnId] ?? true;
                  return (
                    <div className="mt-2 rounded-lg border border-border/80 bg-card/45 p-2.5">
                      <div className="mb-1.5 flex items-center justify-between gap-2">
                        <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground/65">
                          <span>Changed files ({changedFileCountLabel})</span>
                          {hasNonZeroStat(summaryStat) && (
                            <>
                              <span className="mx-1">•</span>
                              <DiffStatLabel
                                additions={summaryStat.additions}
                                deletions={summaryStat.deletions}
                              />
                            </>
                          )}
                        </p>
                        <div className="flex items-center gap-1.5">
                          <Button
                            type="button"
                            size="xs"
                            variant="outline"
                            onClick={() => onToggleAllDirectories(turnSummary.turnId)}
                          >
                            {allDirectoriesExpanded ? "Collapse all" : "Expand all"}
                          </Button>
                          <Button
                            type="button"
                            size="xs"
                            variant="outline"
                            onClick={() =>
                              onOpenTurnDiff(turnSummary.turnId, checkpointFiles[0]?.path)
                            }
                          >
                            View diff
                          </Button>
                        </div>
                      </div>
                      <ChangedFilesTree
                        key={`changed-files-tree:${turnSummary.turnId}`}
                        turnId={turnSummary.turnId}
                        files={checkpointFiles}
                        allDirectoriesExpanded={allDirectoriesExpanded}
                        resolvedTheme={resolvedTheme}
                        onOpenTurnDiff={onOpenTurnDiff}
                      />
                    </div>
                  );
                })()}
                {/* Align the 14px icon with the text, preserving the 28px/24px button. */}
                <div
                  className={cn(
                    "mt-1.5 flex items-center gap-1.5",
                    row.message.text.trim() && "-ml-[7px] sm:-ml-[5px]",
                  )}
                >
                  {row.message.text.trim() && (
                    <MessageCopyButton text={row.message.text} label="Copy response" />
                  )}
                  <p className="text-[10px] text-muted-foreground/80">
                    {formatTimestamp(row.message.createdAt, timestampFormat)}
                  </p>
                </div>
              </div>
            </>
          );
        })()}

      {row.kind === "proposed-plan" && (
        <div className="min-w-0 px-1 py-0.5">
          <ProposedPlanCard
            planMarkdown={row.proposedPlan.planMarkdown}
            cwd={markdownCwd}
            onWorkspaceFileLinkClick={onWorkspaceFileLinkClick}
            workspaceRoot={workspaceRoot}
          />
        </div>
      )}
    </div>
  );

  if (!hasMessages && !isWorking && !showThinkingIndicator) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-muted-foreground/30">
          Send a message to start the conversation.
        </p>
      </div>
    );
  }

  return (
    <div
      ref={timelineRootRef}
      data-timeline-root="true"
      className="mx-auto w-full min-w-0 max-w-3xl overflow-x-hidden"
    >
      {virtualizedRowCount > 0 && (
        <div className="relative" style={{ height: `${rowVirtualizer.getTotalSize()}px` }}>
          {virtualRows.map((virtualRow: VirtualItem) => {
            const row = rows[virtualRow.index];
            if (!row) return null;

            return (
              <div
                key={`virtual-row:${row.id}`}
                data-index={virtualRow.index}
                ref={rowVirtualizer.measureElement}
                className="absolute left-0 top-0 w-full"
                style={{ transform: `translateY(${virtualRow.start}px)` }}
              >
                {renderRowContent(row)}
              </div>
            );
          })}
        </div>
      )}

      {nonVirtualizedRows.map((row) => (
        <div key={`non-virtual-row:${row.id}`}>{renderRowContent(row)}</div>
      ))}
    </div>
  );
});

type TimelineEntry = ReturnType<typeof deriveTimelineEntries>[number];
type TimelineMessage = Extract<TimelineEntry, { kind: "message" }>["message"];
type TimelineProposedPlan = Extract<TimelineEntry, { kind: "proposed-plan" }>["proposedPlan"];
type TimelineWorkEntry = Extract<TimelineEntry, { kind: "work" }>["entry"];
interface TurnTimingDivider {
  id: string;
  state: "working" | "worked";
  beforeEntryId: string | null;
  startedAt: string | null;
  completedAt: string | null;
  summary: string | null;
  expanded: boolean;
  collapsibleEntryCount: number;
}

type TimelineRow =
  | {
      kind: "work";
      id: string;
      createdAt: string;
      groupedEntries: TimelineWorkEntry[];
    }
  | {
      kind: "message";
      id: string;
      createdAt: string;
      message: TimelineMessage;
    }
  | {
      kind: "proposed-plan";
      id: string;
      createdAt: string;
      proposedPlan: TimelineProposedPlan;
    }
  | {
      kind: "turn-timing";
      id: string;
      createdAt: string | null;
      divider: TurnTimingDivider;
    }
  | {
      kind: "thinking";
      id: string;
      createdAt: string | null;
    };

function estimateTimelineProposedPlanHeight(proposedPlan: TimelineProposedPlan): number {
  const estimatedLines = Math.max(1, Math.ceil(proposedPlan.planMarkdown.length / 72));
  return 120 + Math.min(estimatedLines * 22, 880);
}

function formatWorkingTimer(startIso: string, endIso: string): string | null {
  const startedAtMs = Date.parse(startIso);
  const endedAtMs = Date.parse(endIso);
  if (!Number.isFinite(startedAtMs) || !Number.isFinite(endedAtMs)) {
    return null;
  }

  const elapsedSeconds = Math.max(0, Math.floor((endedAtMs - startedAtMs) / 1000));
  if (elapsedSeconds < 60) {
    return `${elapsedSeconds}s`;
  }

  const hours = Math.floor(elapsedSeconds / 3600);
  const minutes = Math.floor((elapsedSeconds % 3600) / 60);
  const seconds = elapsedSeconds % 60;

  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }

  return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
}

function turnTimingDividerLabel(divider: TurnTimingDivider, nowIso: string): string {
  if (divider.state === "working") {
    return divider.startedAt
      ? `Working for ${formatWorkingTimer(divider.startedAt, nowIso) ?? "0s"}`
      : "Working...";
  }

  if (divider.summary) {
    return divider.summary;
  }

  if (divider.startedAt && divider.completedAt) {
    const elapsed = formatWorkingTimer(divider.startedAt, divider.completedAt);
    return elapsed ? `Worked for ${elapsed}` : "Worked";
  }

  return "Worked";
}

const TurnTimingDividerRow = memo(function TurnTimingDividerRow(props: {
  divider: TurnTimingDivider;
  nowIso: string;
  onToggle: (dividerId: string) => void;
}) {
  const { divider, nowIso, onToggle } = props;
  const canToggle = divider.state === "worked" && divider.collapsibleEntryCount > 0;
  const Icon = divider.expanded ? ChevronDownIcon : ChevronRightIcon;
  const label = turnTimingDividerLabel(divider, nowIso);

  return (
    <div className="my-2 flex items-center gap-3 px-1">
      {canToggle ? (
        <button
          type="button"
          className="inline-flex shrink-0 items-center gap-1.5 text-left text-xs font-medium text-muted-foreground/70 transition-colors duration-150 hover:text-foreground/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
          aria-expanded={divider.expanded}
          aria-label={`${label}. ${
            divider.expanded ? "Collapse mid-turn messages" : "Expand mid-turn messages"
          }`}
          onClick={() => onToggle(divider.id)}
        >
          <span>{label}</span>
          <Icon className="size-3.5" />
        </button>
      ) : (
        <span className="inline-flex shrink-0 text-xs font-medium text-muted-foreground/70">
          {label}
        </span>
      )}
      <span className="h-px min-w-8 flex-1 bg-border/80" />
    </div>
  );
});

function formatUserMessageMeta(message: TimelineMessage, timestampFormat: TimestampFormat): string {
  const timestamp = formatTimestamp(message.createdAt, timestampFormat);
  if (!message.author?.name) {
    return timestamp;
  }

  return `${message.author.name} · ${timestamp}`;
}
